import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, RefreshCw, X } from 'lucide-react';
import { appReleaseService, pickLocalizedReleaseChanges, PwaReleaseInfo } from '../../services/appReleaseService';
import { pickLocalized } from '../../utils/localizedContent';
import { useAppLocale } from '../../providers/LocaleProvider';
import { lockPageScroll } from '../../utils/lockPageScroll';
import { hasReleaseChangeDetails, ReleaseChangeGroups } from './ReleaseChangeGroups';
import { ReleaseHeroSection } from './ReleaseHeroSection';
import { LanguageSwitcher } from './LanguageSwitcher';
import { AppBrandLogoFrame } from './AppBrandLogo';
import { emitPwaUpdateBannerVisibility } from '../../utils/pwaUpdateBannerEvents';
import { normalizeAppHostedUploadUrl } from '../../utils/normalizeAppHostedUploadUrl';
import { useAuth } from '../../hooks/useAuth';

const PWA_SHELL_VERSION_KEY = 'pwa-shell-version-seen';
const PWA_EXPANDED_VH = 60;
const PWA_HERO_HEIGHT_RATIO = 0.55;
const EXIT_ANIMATION_MS = 380;
const ENTER_ANIMATION_MS = 380;
const DEFAULT_REMINDER_SECONDS = 300;

type BannerExpandState = 'collapsed' | 'expanded';
type PanelPhase = 'entering' | 'open' | 'closing';

interface PwaUpdateBannerProps {
  className?: string;
}

function snoozeStorageKey(shellVersion: number) {
  return `pwa-update-snooze-until-${shellVersion}`;
}

function isUpdatePending(pwaInfo: PwaReleaseInfo): boolean {
  const seenVersion = Number(localStorage.getItem(PWA_SHELL_VERSION_KEY) ?? '0');
  return pwaInfo.shellVersion > seenVersion && !!pwaInfo.activeReleaseId;
}

function isSnoozed(shellVersion: number): boolean {
  const until = Number(sessionStorage.getItem(snoozeStorageKey(shellVersion)) ?? '0');
  return until > Date.now();
}

function BannerExpandChevron({ expanded }: { expanded: boolean }) {
  return (
    <ChevronDown
      className={`h-3.5 w-3.5 text-gray-600 transition-transform duration-300 ease-in-out dark:text-gray-300 ${
        expanded ? 'rotate-180' : 'rotate-0'
      }`}
      aria-hidden
    />
  );
}

const PwaUpdateBanner: React.FC<PwaUpdateBannerProps> = ({ className = '' }) => {
  const { t } = useTranslation('common');
  const { locale } = useAppLocale();
  const { user } = useAuth();
  const [info, setInfo] = useState<PwaReleaseInfo | null>(null);
  const [mounted, setMounted] = useState(false);
  const [panelPhase, setPanelPhase] = useState<PanelPhase>('entering');
  const [refreshing, setRefreshing] = useState(false);
  const [expandState, setExpandState] = useState<BannerExpandState>('expanded');
  const [snoozeTick, setSnoozeTick] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(
    () => (typeof window !== 'undefined' ? window.innerHeight : 800)
  );
  const [headerHeight, setHeaderHeight] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const exitTimerRef = useRef<number | null>(null);
  const detailsId = useId();

  const reminderSeconds = info?.updateReminderSeconds ?? DEFAULT_REMINDER_SECONDS;

  const shouldOpen =
    info != null && isUpdatePending(info) && !isSnoozed(info.shellVersion);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const pwaInfo = await appReleaseService.getPwaReleaseInfo();
      if (cancelled) return;
      setInfo(pwaInfo);
    };

    void load();
    const interval = window.setInterval(load, 5 * 60 * 1000);
    const onFocus = () => void load();
    window.addEventListener('focus', onFocus);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  useEffect(() => {
    if (!info || !isUpdatePending(info)) return undefined;
    if (!isSnoozed(info.shellVersion)) return undefined;

    const until = Number(sessionStorage.getItem(snoozeStorageKey(info.shellVersion)) ?? '0');
    const delay = Math.max(until - Date.now(), 0);
    const timerId = window.setTimeout(() => setSnoozeTick((value) => value + 1), delay + 50);
    const heartbeatId = window.setInterval(() => setSnoozeTick((value) => value + 1), 1000);

    return () => {
      window.clearTimeout(timerId);
      window.clearInterval(heartbeatId);
    };
  }, [info, reminderSeconds, snoozeTick]);

  useEffect(() => {
    if (shouldOpen) {
      setMounted(true);
      setPanelPhase('entering');
      setExpandState('expanded');
      const enterTimer = window.setTimeout(() => setPanelPhase('open'), 24);
      return () => window.clearTimeout(enterTimer);
    }

    if (!mounted) return undefined;

    setPanelPhase('closing');
    exitTimerRef.current = window.setTimeout(() => {
      setMounted(false);
      setPanelPhase('entering');
    }, EXIT_ANIMATION_MS);

    return () => {
      if (exitTimerRef.current != null) {
        window.clearTimeout(exitTimerRef.current);
        exitTimerRef.current = null;
      }
    };
  }, [shouldOpen, mounted]);

  const blocksPageChrome = mounted;

  useEffect(() => {
    emitPwaUpdateBannerVisibility(blocksPageChrome);
    return () => {
      if (blocksPageChrome) emitPwaUpdateBannerVisibility(false);
    };
  }, [blocksPageChrome]);

  useEffect(() => {
    if (!blocksPageChrome) return undefined;
    return lockPageScroll();
  }, [blocksPageChrome]);

  useEffect(() => {
    const onResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const headerEl = headerRef.current;
    if (!headerEl) return;
    const ro = new ResizeObserver(() => setHeaderHeight(headerEl.offsetHeight));
    ro.observe(headerEl);
    setHeaderHeight(headerEl.offsetHeight);
    return () => ro.disconnect();
  }, [mounted, info]);

  const handleDismiss = () => {
    if (!info) return;
    const reminderMs = Math.max(reminderSeconds, 30) * 1000;
    sessionStorage.setItem(snoozeStorageKey(info.shellVersion), String(Date.now() + reminderMs));
    setSnoozeTick((value) => value + 1);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      if (info) {
        localStorage.setItem(PWA_SHELL_VERSION_KEY, String(info.shellVersion));
        sessionStorage.removeItem(snoozeStorageKey(info.shellVersion));
      }
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) await registration.update();
      window.location.reload();
    } finally {
      setRefreshing(false);
    }
  };

  if (!mounted || !info) return null;

  const isFa = locale === 'fa';
  const headline = info.versionLabel
    ? t('pwa.versionReady', { version: info.versionLabel })
    : t('pwa.updateReady');
  const localizedTitle = pickLocalized(info.title, info.titleFa, locale);
  const localizedSummary = pickLocalized(info.summary, info.summaryFa, locale);
  const localizedChanges = pickLocalizedReleaseChanges(info.changes, info.changesFa, locale);
  const hasGroupedChanges = hasReleaseChangeDetails(localizedChanges);
  const hasExpandableDetails = Boolean(localizedTitle || localizedSummary || hasGroupedChanges);
  const heroImageUrl = info.heroImageUrl?.trim() ? normalizeAppHostedUploadUrl(info.heroImageUrl) : '';
  const isOffScreen = panelPhase === 'closing' || panelPhase === 'entering';
  const isBackdropVisible = panelPhase === 'open';

  const toggleExpandState = () => {
    if (!hasExpandableDetails) return;
    setExpandState((current) => (current === 'collapsed' ? 'expanded' : 'collapsed'));
  };

  const isExpanded = expandState === 'expanded';
  const expandedCapPx = viewportHeight * (PWA_EXPANDED_VH / 100);
  const heroImageHeightPx = Math.round(expandedCapPx * PWA_HERO_HEIGHT_RATIO);

  const panelMaxHeightPx =
    expandState === 'collapsed'
      ? headerHeight > 0
        ? headerHeight
        : undefined
      : expandedCapPx;

  const panelScrollMaxPx =
    isExpanded && headerHeight > 0 ? Math.max(expandedCapPx - headerHeight, 0) : undefined;

  const expandAriaLabel = isExpanded
    ? t('pwa.collapseDetails', { defaultValue: 'Collapse details' })
    : t('pwa.expandDetailsMore', { defaultValue: 'Show release details' });

  const panelMotionMs = panelPhase === 'entering' ? ENTER_ANIMATION_MS : EXIT_ANIMATION_MS;

  return createPortal(
    <>
      <div
        className={`fixed inset-0 z-[240] bg-black/45 backdrop-blur-[2px] transition-opacity ease-out ${
          isBackdropVisible ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ transitionDuration: `${panelMotionMs}ms` }}
        aria-hidden
      />

      <div
        ref={panelRef}
        className={`fixed top-0 left-1/2 z-[250] flex w-[calc(100%-1.5rem)] max-w-[calc(100%-1.5rem)] flex-col overflow-hidden rounded-b-2xl border border-gray-200 bg-white shadow-[0_24px_60px_-12px_rgba(0,0,0,0.35),0_12px_32px_-10px_rgba(0,0,0,0.2)] ring-1 ring-black/5 transition-[max-height,transform,opacity] ease-in-out dark:border-gray-700 dark:bg-gray-900 dark:shadow-[0_28px_70px_-12px_rgba(0,0,0,0.65),0_14px_36px_-12px_rgba(0,0,0,0.45)] dark:ring-white/10 md:w-[56%] md:max-w-[56%] ${
          isBackdropVisible ? 'opacity-100' : 'opacity-0'
        } ${className}`}
        style={{
          ...(panelMaxHeightPx != null ? { maxHeight: `${panelMaxHeightPx}px` } : {}),
          transform: isOffScreen ? 'translate(-50%, -108%)' : 'translate(-50%, 0)',
          transitionDuration: `${panelMotionMs}ms`,
        }}
        role="dialog"
        aria-modal="true"
        aria-live="polite"
      >
        <div
          ref={headerRef}
          dir={isFa ? 'rtl' : 'ltr'}
          className="relative z-30 flex shrink-0 items-start gap-2 border-b border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-900 sm:items-center sm:gap-3"
        >
          <div className="flex min-w-0 flex-1 items-start gap-3 sm:items-center">
            <AppBrandLogoFrame size="sm" alt="" className="mt-0.5 sm:mt-0" />

            {hasExpandableDetails ? (
              <>
                <button
                  type="button"
                  onClick={toggleExpandState}
                  aria-controls={detailsId}
                  className={`min-w-0 flex-1 text-sm font-semibold text-gray-900 dark:text-gray-100 ${
                    isFa ? 'text-right font-persian' : 'text-left'
                  }`}
                  aria-label={expandAriaLabel}
                >
                  {headline}
                </button>
                <button
                  type="button"
                  onClick={toggleExpandState}
                  aria-expanded={isExpanded}
                  aria-controls={detailsId}
                  className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 sm:mt-0"
                  aria-label={expandAriaLabel}
                >
                  <BannerExpandChevron expanded={isExpanded} />
                </button>
              </>
            ) : (
              <p
                className={`min-w-0 flex-1 text-sm font-semibold text-gray-900 dark:text-gray-100 ${
                  isFa ? 'text-right font-persian' : 'text-left'
                }`}
              >
                {headline}
              </p>
            )}
          </div>

          <div className="flex shrink-0 items-start gap-2">
            <div className="flex flex-col items-stretch gap-1.5">
              <button
                type="button"
                onClick={() => void handleRefresh()}
                disabled={refreshing}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-amber-700 disabled:opacity-60"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} aria-hidden />
                {t('pwa.updateButton')}
              </button>
              <LanguageSwitcher variant="compact" className="shrink-0 self-center" persistUser={Boolean(user)} />
            </div>
            <button
              type="button"
              onClick={handleDismiss}
              className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 dark:hover:bg-gray-800 dark:hover:text-gray-200"
              aria-label={t('pwa.dismissUpdate')}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {hasExpandableDetails && (
          <div
            id={detailsId}
            ref={scrollRef}
            className={`relative min-h-0 shrink-0 border-t border-gray-200 bg-white transition-[max-height,opacity,transform] duration-300 ease-in-out dark:border-gray-700 dark:bg-gray-900 ${
              isExpanded
                ? 'overflow-y-auto overscroll-y-contain [-webkit-overflow-scrolling:touch]'
                : 'overflow-hidden'
            } ${isExpanded ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-2 opacity-0'}`}
            style={panelScrollMaxPx != null ? { maxHeight: `${panelScrollMaxPx}px` } : undefined}
            dir={isFa ? 'rtl' : 'ltr'}
          >
            <div className={`relative z-10 space-y-3 px-4 pb-3 pt-3 ${isFa ? 'font-persian' : ''}`}>
              {(localizedTitle || localizedSummary || heroImageUrl) && (
                <ReleaseHeroSection
                  title={localizedTitle}
                  summary={localizedSummary}
                  heroImageUrl={heroImageUrl}
                  textFromPercent={55}
                  imageBlurClass="blur-[1px]"
                  heightPx={heroImageUrl && isExpanded ? heroImageHeightPx : undefined}
                  titleClassName="text-base font-semibold"
                  summaryClassName="text-sm leading-relaxed"
                  scrollableSummary
                  rtl={isFa}
                />
              )}

              {hasGroupedChanges && <ReleaseChangeGroups changes={localizedChanges} compact />}
            </div>
          </div>
        )}
      </div>
    </>,
    document.body
  );
};

export default PwaUpdateBanner;
